import React, { useState, useEffect } from 'react';
import { createAliasForAsset, deleteAliasForAsset, fetchAliasesForAsset, invalidateAliasForAsset, invalidateCreateAliasForAsset, updateAliasForAsset } from "actions/assetAliases";
import { useDispatch, useSelector } from "react-redux";

import cx from 'classnames';
import _ from 'lodash';
import { toast } from "react-toastify";
import { SuccessToast, FailureToast } from "components/Toast";

import { SmallContentCard } from "..";
import { Accordion, Badge, Button, Form, Dropdown } from "react-bootstrap";
import { EditorContext } from "components/Editor";
import InfiniteScroll from "components/InfiniteScroll";
import { PrimaryButton, SecondaryButton } from "components/Buttons";
import moment from "moment";
import { invalidatePageQuery, queryPages } from "actions/pages";
import { IKImage } from "imagekitio-react";
import Tooltip from 'components/Tooltip';
import LinesEllipsis from 'react-lines-ellipsis';
import TruncatedTextWithTooltip from "components/TruncatedTextWithTooltip";
import styles from './aliases.module.scss';

const defaultContentImage = '/public/cleverstory/v2/static/default_card.jpg';

export const AssetAliases = ({ editorSession, content, handlePropertyChange }) => {
  const dispatch = useDispatch();

  const id = _.get(content, 'id');

  const [selected, setSelected] = useState(null);
  const [create, setCreate] = useState(false);

  const { loading, aliases, pagination, hasMoreItems } = useSelector((state) => {
    const aliasState = _.get(state, 'content.aliases.id');
    if (aliasState != id) {
      return {
        loading: false,
        aliases: [],
        pagination: null,
        hasMoreItems: false,
      }
    }

    const isLoading = _.get(state, 'content.aliases.loading');
    const aliases = _.get(state, 'content.aliases.items');
    const pagination = _.get(state, 'content.aliases.pagination');
    const hasMoreItems = _.get(pagination, "currentPage") < _.get(pagination, "totalPages") - 1;

    return {
      loading: isLoading,
      aliases,
      pagination,
      hasMoreItems,
    }
  });

  const updating = useSelector((state) => _.get(state, 'content.aliases.update.updating'));
  const creating = useSelector((state) => _.get(state, 'content.aliases.create.creating'));
  const created = useSelector((state) => _.get(state, 'content.aliases.create.created'));

  const loadMoreAliases = () => {
    const nextPage = pagination.currentPage + 1;
    if (nextPage >= pagination.totalPages) {
      return;
    }
    fetchAliases(nextPage);
  }

  const fetchAliases = (page) => {
    const filterRequest = {
      type: 'asset',
      target: id,
    }

    dispatch(fetchAliasesForAsset(filterRequest, page, 10));
  }

  const handleSelectionChanged = (id) => {
    if (!id) {
      setSelected(null);
      setCreate(false);
    }

    const alias = aliases.find(item => item.id === id);
    setSelected(alias);
  }

  const handleCreateClicked = () => {
    setCreate(true);
  }

  useEffect(() => {
    if (content) {
      setCreate(false);
      dispatch(invalidateAliasForAsset());
      fetchAliases(0);
    }
  }, [content]);

  useEffect(() => {
    if (created) {
      setCreate(false);
      setSelected(created);
      dispatch(invalidateCreateAliasForAsset());
    }
  }, [created]);

  return (
    <div className={styles.content}>
      <div className={cx(styles.header)}>
        <div className={styles.top}>
          <h1 className={styles.title} >Manage Aliases</h1>
          <div className={styles.actions}>
            <Button size='sm' className={cx(styles.action, styles.add)}
              variant='secondary'
              disabled={updating || creating}
              onClick={handleCreateClicked}
            >
              <i className={cx(styles.icon, 'cleverstory-plus')} />
              Add Alias
            </Button>
          </div>
        </div>
        <p className={styles.description} >Additional user-defined data fields for specific information collection.</p>
      </div>
      <div className={styles.asset}>
        <SmallContentCard content={_.get(content, 'asset')} />
      </div>
      <InfiniteScroll className={styles.scrollContainer} loading={loading} hasMore={hasMoreItems} loadMore={loadMoreAliases}>
        {loading && _.size(aliases) === 0 &&
          <div className={styles.loading}>Loading aliases...</div>
        }
        <Accordion activeKey={create ? 'create-alias' : _.get(selected, 'id')} onSelect={handleSelectionChanged}>
          {create &&
            <CreateAssetAliasCard asset={content} onCancel={() => setCreate(false)} />
          }
          {aliases.map((alias, index) => {
            const active = _.get(selected, 'id') === alias.id;
            return <AssetAliasCard
              editorSession={editorSession}
              key={alias.id}
              alias={alias}
              selected={active}
              canDelete={_.size(aliases) > 1}
            />
          })}
        </Accordion>
      </InfiniteScroll>
    </div>
  )
}

export const CreateAssetAliasCard = ({ asset, onCancel }) => {
  const dispatch = useDispatch();
  const [editedAlias, setEditedAlias] = useState({
    url: '/untitled',
    autoCreate: false,
    canonical: false,
  });

  const user = useSelector(state => _.get(state, 'users.me'));
  const creating = useSelector((state) => _.get(state, 'content.aliases.create.creating'));
  const serverErrors = useSelector(state => {
    return _.get(state, `content.aliases.create.error.errors`);
  });

  const aliases = useSelector((state) => _.get(state, 'content.aliases'));

  const handleAliasChange = (value) => {
    setEditedAlias(value);
  }

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  }

  const create = useSelector((state) => _.get(state, 'content.aliases.create'));

  const renderToastName = () => {
    var name = 'Alias'
    return name;
  }

  useEffect(() => {
    if (!create.created) {
      return;
    }

    if (create.created) {
      toast(
        <SuccessToast
          heading={`Success`}
          description={`${renderToastName()} created successfully`}
        />,
        {
          type: toast.TYPE.DEFAULT,
          position: toast.POSITION.BOTTOM_RIGHT,
          autoClose: 3000,
        }
      );

      dispatch(invalidateCreateAliasForAsset());
    } else if (create.error) {
      toast(
        <FailureToast
          heading={`Failure`}
          description={`Unable to create ${renderToastName()} asset alias`}
        />,
        {
          type: toast.TYPE.DEFAULT,
          position: toast.POSITION.BOTTOM_RIGHT,
          autoClose: 3000,
        }
      );

      dispatch(invalidateCreateAliasForAsset());
    }
  }, [create]);

  const handleSave = () => {
    const url = _.get(editedAlias, 'url');
    const autoCreate = _.get(editedAlias, 'autoCreate');
    const canonical = _.get(editedAlias, 'canonical');
    const pageId = _.get(editedAlias, 'page')

    const request = {
      type: 'asset',
      target: asset.id,
      url: url,
      autoCreate: autoCreate,
      pageId: pageId,
      page: _.get(pageId, "id"),
      canonical: canonical,
    }

    dispatch(createAliasForAsset(request));
  }

  return (
    <Accordion.Item className={styles.alias} eventKey={`create-alias`}>
      <Accordion.Header>
        <div className={styles.details}>
          <div className={styles.metadata}>
            <span className={styles.created}>Today</span>
          </div>
          <div className={styles.url}>/untitled</div>
          <div className={styles.author}>Created by: {`${_.get(user, 'firstName')} ${_.get(user, 'lastName')}`}</div>
        </div>
        <div className={styles.actions}>
          <Button className={cx(styles.arrow)} variant="info">
            <i className={cx(styles.icon, 'cleverstory-drop-down')} />
          </Button>
        </div>
      </Accordion.Header>
      <Accordion.Body>
        {editedAlias &&
          <AliasDetailsEditor alias={editedAlias}
            actionLabels={{
              'save': 'Create Alias',
              'saving': 'Creating...',
              'reset': 'Cancel',
            }}
            saving={creating}
            serverErrors={serverErrors}
            canDelete={false}
            onChange={handleAliasChange}
            onReset={handleCancel}
            onSave={handleSave}
          />
        }
      </Accordion.Body>
    </Accordion.Item>
  )
}

export const AssetAliasCard = ({ editorSession, alias, selected, canDelete }) => {
  const dispatch = useDispatch();
  const [editedAlias, setEditedAlias] = useState(null);
  const [selectedDomain,setSelectedDomain] = useState({});

  const storyboard = useSelector((state) => _.get(state, "storyboards.storyboard.item", null));
  const updating = useSelector((state) => _.get(state, 'content.aliases.update.updating'));
  const aliasValue = useSelector(state => _.get(state, 'content.aliases.items', []).find(item => item.id === alias.id));
  const updated = aliasValue.updated;
  const serverErrors = useSelector(state => {
    const editingAlias = _.get(state, `content.aliases.update.id`);
    if (editingAlias === alias.id) {
      const updateError = _.get(state, `content.aliases.update.error.errors`);
      if (updateError) {
        return updateError;
      }
    }

    const deletingAlias = _.get(state, `content.aliases.delete.id`);
    if (deletingAlias === alias.id) {
      return _.get(state, `content.aliases.delete.error.errors`);
    }

    return [];
  });
  var domain = useSelector(state => _.get(state, 'workspaces.workspace.item.defaultDomain') || "");
  const liveUrl = `${selectedDomain.url}${alias.url}`;

  const handleAliasChange = (value) => {
    setEditedAlias(value);
  }

  const handleReset = () => {
    const value = _.cloneDeep(alias);
    setEditedAlias(value);
  }

  const handleSave = () => {
    const url = _.get(editedAlias, 'url');
    const autoCreate = _.get(editedAlias, 'autoCreate');
    const canonical = _.get(editedAlias, 'canonical');
    const pageId = _.get(editedAlias, 'page.id')

    const request = {
      type: 'asset',
      url: url,
      autoCreate: autoCreate,
      page: pageId,
      canonical: canonical,
    }
    dispatch(updateAliasForAsset(alias.id, request, alias.url));
  }

  const renderToastName = () => {
    var name = 'Alias'
    return name;
  }

  useEffect(()=>{
    let domainUrl = _.get(storyboard,'domain') || domain;
    setSelectedDomain(domainUrl);
  },[storyboard, domain])
  
  useEffect(() => {
    if (!updated) {
      return;
    }

    if (updated) {
      toast(
        <SuccessToast
          heading={`Success`}
          description={`${renderToastName()} saved successfully`}
        />,
        {
          type: toast.TYPE.DEFAULT,
          position: toast.POSITION.BOTTOM_RIGHT,
          autoClose: 3000,
        }
      );

      dispatch(invalidateCreateAliasForAsset());
    } else if (updated.error) {
      toast(
        <FailureToast
          heading={`Failure`}
          description={`Unable to save ${renderToastName()} asset alias`}
        />,
        {
          type: toast.TYPE.DEFAULT,
          position: toast.POSITION.BOTTOM_RIGHT,
          autoClose: 3000,
        }
      );

      dispatch(invalidateCreateAliasForAsset());
    }
  }, [updated]);


  const handleLiveUrlClicked = (e) => {
    e.stopPropagation();
    window.open(liveUrl);
  }

  useEffect(() => {
    if (selected) {
      const value = _.cloneDeep(alias);
      setEditedAlias(value);
    } else {
      setEditedAlias(null);
    }

  }, [selected, alias]);

  useEffect(() => {
    dispatch(invalidatePageQuery());
  }, []);


  return (
    <Accordion.Item className={styles.alias} eventKey={`${alias.id}`}>
      <Accordion.Header>
        <div className={styles.details}>
          <div className={styles.metadata}>
            <span className={styles.created}>{moment(alias.modifiedDate).fromNow()}</span>
            {alias.canonical &&
              <Badge className={styles.badge} bg="warning">
                Canonical
              </Badge>
            }
          </div>
          <div className={styles.url}>
            <TruncatedTextWithTooltip content={alias.url} maxWidth="150" placement="top" />

            {/* {alias.url} */}
          </div>
          <div className={styles.author}>Created by: {`${_.get(alias, 'createdBy.firstName')} ${_.get(alias, 'createdBy.lastName')}`}</div>
        </div>
        <div className={styles.actions}>
          <Tooltip placement="top" content={liveUrl}>
            <Button variant="info" onClick={handleLiveUrlClicked}>
              <i className={cx(styles.icon, 'cleverstory-url')} />
            </Button>
          </Tooltip>
          <Button className={cx(styles.arrow)} variant="info">
            <i className={cx(styles.icon, 'cleverstory-drop-down')} />
          </Button>
        </div>
      </Accordion.Header>
      <Accordion.Body>
        {editedAlias &&
          <AliasDetailsEditor 
            editorSession={editorSession}
            alias={editedAlias}
            actionLabels={{
              'save': 'Save Changes',
              'saving': 'Saving...',
              'reset': 'Reset',
            }}
            saving={updating}
            serverErrors={serverErrors}
            canDelete={canDelete && !alias.canonical}
            canonical={alias.canonical}
            onChange={handleAliasChange}
            onReset={handleReset}
            onSave={handleSave}
          />
        }
      </Accordion.Body>
    </Accordion.Item>
  )
}

const AliasDetailsEditor = ({ editorSession, alias, actionLabels, saving, serverErrors, canDelete, canonical, onChange, onSave, onReset }) => {
  const dispatch = useDispatch();

  const [deletion, setDeletion] = useState(false);
  const [mapAsset, setMapAsset] = useState(false);
  const [errors, setErrors] = useState({});
  const [query, setQuery] = useState('');
  const selectedPage = _.get(alias, 'page', null);
  const deleting = useSelector((state) => _.get(state, 'content.aliases.delete.deleting'));
  const pageResult = useSelector((state) => _.get(state, 'pages.pageQuery.items', []));
  const storyboardEntity = useSelector((state) => _.get(state, 'storyboards.storyboard.item'));
  const disabled = deleting || saving;

  const layoutContents = useSelector(state => _.get(state, `${editorSession}.present.layoutManager.items`) || []);
  const content = useSelector((state) => _.get(state, 'content', null));
  const contentId = _.get(content, 'aliases.id', null);
  const aliases = _.get(content, 'aliases.items', []);
  
  const handleAliasChange = (delta) => {
    if (onChange) {
      const value = {
        ...alias,
        ...delta,
      }
      onChange(value);
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      const request = {
        storyboard: storyboardEntity.id,
        query: query
      }

      dispatch(queryPages(request, 0, 50))
    }
  }

  useEffect(() => {
    if (selectedPage) {
      setMapAsset(true);
    }
  }, [selectedPage])


  const handleInputChange = (e) => {
    setQuery(e.target.value);
  }

  const handlePageChange = (page) => {
    handleAliasChange({
      page: page
    })
  };

  const handleUrlChange = (e) => {
    handleAliasChange({
      url: e.target.value
    });
  }

  const handleAutoCreateChange = (e) => {
    handleAliasChange({
      autoCreate: e.target.checked
    });
  }

  const handleCanonicalChange = (e) => {
    handleAliasChange({
      canonical: e.target.checked
    });
  }

  const handleReset = () => {
    if (onReset) {
      onReset();
    }
  }

  const handleSave = () => {
    const url = (_.get(alias, 'url') || '').trim();
    const autoCreate = _.get(alias, 'autoCreate');

    let errors = {};
    var aliasRegex = /^[a-z_-]|[`\s!@#$%^&*()+\=\[\]{};':"\\|,.<>\?~]/;
    var slashRegex = /([^:]\/)\/+/


    if (!autoCreate) {
      if (url === '' || aliasRegex.test(url) || slashRegex.test(url)) {
        errors['url'] = 'Please provide valid alias';
      } else if (url.startsWith('//')) {
        errors['url'] = 'Alias should begin with /';
      } else if (_.size(url) > 1500) {
        errors['url'] = 'Alias should less than 1500 characters';
      }
    }

    setErrors(errors);

    if (_.size(_.keys(errors)) === 0) {
      onSave();
    }
  }

  const handleDeleteClicked = (e) => {
    setDeletion(true);
  }

  const handleDeleteConfirmation = (e) => {
    const aliasUsedInLayout = layoutContents.some(item => item.content.id == contentId && (_.get(item, 'url') == alias.url));
    const canonicalUrl = aliases.find(item => item.canonical)?.url;

    const onDeleteRequest = {
      editorSession,
      contentId,
      canonicalUrl,
      aliasUsedInLayout
    }

    if (alias.id) {
      dispatch(deleteAliasForAsset(alias.id, onDeleteRequest));
    }
  }

  const handleAssetMapToggle = () => {
    setMapAsset(!mapAsset);
    handleCancelSelection()
  }

  const handleCancelSelection = () => {
    handleAliasChange({
      page: null
    })
  }

  useEffect(() => {
    if (_.size(serverErrors) > 0) {
      const errorMap = { ...errors };
      serverErrors.forEach(item => {
        if (item.code === 35001) {
          errorMap['url'] = "Alias already exists";
        } else if (item.code === 35003) {
          errorMap['url'] = "Canonical alias cannot be deleted";
        }
      });

      setErrors(errorMap);
    }
  }, [serverErrors]);

  return (
    <div className={styles.editAlias}>
      <div className={styles.nameField}>
        <div className={styles.header}>
          <span className={styles.label}>URL Name</span>
          {canDelete &&
            <>
              {deleting &&
                <div className={styles.deleting}>deleting...</div>
              }
              {!deleting &&
                <Button variant="info" onClick={handleDeleteClicked}>
                  <i className={cx(styles.icon, 'cleverstory-trash')} />
                </Button>
              }
            </>
          }
        </div>
        <Form.Control type="text" className={styles.inputField}
          value={alias.url}
          disabled={alias.autoCreate || disabled}
          onChange={handleUrlChange}
        />
        <Form.Check // prettier-ignore
          type="checkbox"
          className={styles.autoGenerateCheck}
          label="Autogenerate URL from name"
          disabled={disabled}
          checked={alias.autoCreate}
          onChange={handleAutoCreateChange}
        />

        {errors['url'] &&
          <div className={styles.error}>{errors['url']}</div>
        }
      </div>

      <div className={styles.canonicalField}>
        <div className={styles.switchAction}>
          <span className={styles.label}>Mark as canonical</span>
          <Form.Check
            type="switch"
            name="canonical"
            disabled={disabled || canonical}
            className={styles.toggleSwitch}
            checked={_.get(alias, 'canonical', false)}
            onChange={handleCanonicalChange}
          />
        </div>
        <span className={styles.subtext}>This alias will be used as a default url</span>
      </div>

      {storyboardEntity.type != 'INTERACTIVE_CONTENT' && <div className={styles.assetMap}>
        <div className={styles.switchAction}>
          <span className={styles.label}>Map asset with Page</span>
          <Form.Check
            type="switch"
            checked={mapAsset}
            onClick={handleAssetMapToggle}
            className={styles.toggleSwitch}
          />
        </div>
        {(mapAsset && !selectedPage) &&
          <Dropdown drop='up' className={styles.pageDropdown}>
            <Dropdown.Toggle as={Form.Group} id="dropdown-search" className={styles.pageDropdownToggle}>
              <Form.Control type="text"
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder="Search" />
            </Dropdown.Toggle>
            {_.size(pageResult) > 0 &&
              <Dropdown.Menu className={styles.pageMenu}>
                {pageResult.map((result, index) => {
                  return (
                    <Dropdown.Item key={index} eventKey={result.id}>
                      <PageDataCard selectedPage={selectedPage} handleSelect={handlePageChange} pageData={result} key={index} />
                    </Dropdown.Item>
                  )
                })}
              </Dropdown.Menu>
            }
          </Dropdown>}
        {selectedPage &&
          <PageDataCard
            pageData={selectedPage}
            selected={true}
            onCancel={handleCancelSelection}
          />
        }
      </div>}

      <div className={styles.actions}>
        <SecondaryButton
          className={cx(styles.action, styles.reset)}
          text={actionLabels['reset']}
          disabled={disabled}
          onClick={handleReset}
        />
        <PrimaryButton
          className={cx(styles.action, styles.save)}
          text={saving ? actionLabels['saving'] : actionLabels['save']}
          disabled={disabled}
          onClick={handleSave}
        />
      </div>
      {deletion &&
        <div className={styles.deleteConfirmation}>
          <div className={styles.inner}>
            <p>Are you sure you would like to delete <b>{alias.url}</b></p>
            <div className={styles.actions}>
              <PrimaryButton
                className={cx(styles.action, styles.delete)}
                text={deleting ? 'Deleting...' : 'Delete'}
                disabled={deleting}
                onClick={handleDeleteConfirmation}
              />
              <SecondaryButton
                className={cx(styles.action, styles.reset)}
                text={'Cancel'}
                disabled={deleting}
                onClick={() => { setDeletion(false) }}
              />
            </div>
          </div>
        </div>
      }
    </div>
  )
}

const PageDataCard = ({ selectedPage, handleSelect, pageData, selected, onCancel, key, className }) => {
  const getFieldSelector = (mapping, key) => {
    const fields = _.get(mapping, 'fields') || [];
    const field = fields.find(item => item.name === key);

    return field;
  }

  const [tooltip, setTooltip] = useState(false)

  const getContentForFieldSelector = (mapping, key) => {
    const field = getFieldSelector(mapping, key);

    if (!field) {
      return null;
    }

    return _.get(field, `content`);
  }

  const handleClick = (e) => {
    if (!handleSelect) {
      return;
    }

    handleSelect(pageData);
  }

  const handleTooltip = (truncated) => {
    const { clamped } = truncated;
    setTooltip(clamped);
  }

  var name = _.get(pageData, 'name', '');
  var iconData = _.get(pageData, 'icon');
  var iconUrl = _.get(iconData, 'url', null);

  var alias = _.get(pageData, 'canonical')

  return (
    <div key={key} onClick={handleClick} className={cx(styles.pagesWrapper, className, {
      [styles.selected]: selected
    })}>
      <div className={styles.iconWrapper}>
        <IKImage
          className={styles.icon}
          path={iconUrl || defaultContentImage}
        />
      </div>

      <div className={styles.detailsContainer}>
        <div className={styles.metaData}>
          <TruncatedTextWithTooltip content={name} className={styles.name} maxWidth='100' placement="top" />
          <TruncatedTextWithTooltip content={alias} className={styles.alias} maxWidth='100' placement="top" />
        </div>
        {selected &&
          <Button variant='info' className={styles.closeBtn} onClick={onCancel}>
            <i className={cx('cleverstory-close')} />
          </Button>
        }
      </div>
      
    </div>
  )
}